/**
 * Mobile state store — Task 7
 * Reads from localStorage (populated by SyncEngine pull).
 * Mirrors FOM mobile.html fetchCloud → setPersonnel / setShiftInfo.
 */
import { create } from 'zustand'
import type { PersonnelEntry, SecondaryTask } from './desktop'
import { scheduleOfficerNotifications } from '../services/notifications'

export interface ShiftInfo {
  date: string
  shift: string
}

interface MobileState {
  personnel: PersonnelEntry[]
  secondaryTasks: SecondaryTask[]
  shiftInfo: ShiftInfo
  notes: string[]
  hydrateFromStorage: () => void
}

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) as T : fallback
  } catch { return fallback }
}

export const useMobileStore = create<MobileState>((set) => ({
  personnel: load('fom_personnel', []),
  secondaryTasks: load('fom_secondaryTasks', []),
  shiftInfo: { date: '', shift: '' },
  notes: load('fom_notes', []),

  hydrateFromStorage() {
    const personnel = load<PersonnelEntry[]>('fom_personnel', [])
    const secondaryTasks = load<SecondaryTask[]>('fom_secondaryTasks', [])
    const notes = load<string[]>('fom_notes', [])

    // Build shiftInfo from fom_security_summary
    let shiftInfo: ShiftInfo = { date: '', shift: '' }
    try {
      const raw = localStorage.getItem('fom_security_summary')
      if (raw) {
        const s = JSON.parse(raw)
        shiftInfo = { date: s.date ?? '', shift: s.shift ?? '' }
      }
    } catch { /* silent */ }

    if (!shiftInfo.date) {
      shiftInfo.date = load('fom_currentDate', new Date().toISOString().slice(0, 10))
    }

    set({ personnel, secondaryTasks, shiftInfo, notes })

    // Schedule shift/break end notifications for the logged-in officer
    try {
      const sessionRaw = localStorage.getItem('fom_auth_session')
      if (sessionRaw) {
        const session = JSON.parse(sessionRaw) as { callSign?: string }
        const cs = (session.callSign || '').toUpperCase()
        if (cs) {
          const me = personnel.find(p => (p.callSign || '').toUpperCase() === cs)
          if (me && (me as any).shiftTo) {
            const breaks = (me.posts || []).filter((p: any) => p.isBreak || p.post === 'Break')
              .map((p: any) => ({ from: p.from || p.time, to: p.to || p.endTime }))
            const myTasks = secondaryTasks.filter(t =>
              (t.callSign || '').toUpperCase() === cs
            )
            scheduleOfficerNotifications(cs, (me as any).shiftTo, breaks, myTasks).catch(() => {})
          }
        }
      }
    } catch { /* silent */ }
  },
}))

// Hydrate on load
useMobileStore.getState().hydrateFromStorage()

// Request notification permission proactively on first load (Android)
if (typeof window !== 'undefined') {
  import('@capacitor/core').then(({ Capacitor }) => {
    if (Capacitor.isNativePlatform()) {
      import('@capacitor/local-notifications' as any).then(({ LocalNotifications }) => {
        LocalNotifications?.requestPermissions?.().catch(() => {})
      }).catch(() => {})
    }
  }).catch(() => {})
}

// Re-hydrate immediately when a sync pull completes
if (typeof window !== 'undefined') {
  window.addEventListener('castellan:pull-complete', () => {
    useMobileStore.getState().hydrateFromStorage()
  })
  // Also re-hydrate when posting/desktop notes change locally
  window.addEventListener('castellan:posting-changed', () => {
    useMobileStore.getState().hydrateFromStorage()
  })

  // Re-hydrate + force pull when app comes back to foreground.
  // On Android, Capacitor's App.appStateChange is far more reliable than
  // document.visibilitychange (which is often skipped or delayed by the WebView).
  // We register both: the native listener fires first on Capacitor, visibilitychange
  // acts as a fallback for browser/iOS.
  import('@capacitor/core').then(({ Capacitor }) => {
    if (Capacitor.isNativePlatform()) {
      import('@capacitor/app').then(({ App }) => {
        App.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
          if (isActive) {
            useMobileStore.getState().hydrateFromStorage()
            import('./sync').then(({ useSyncStore }) => {
              useSyncStore.getState().forcePull()
            }).catch(() => {})
          }
        })
      }).catch(() => {})
    }
  }).catch(() => {})

  // Fallback: visibilitychange for browser / iOS WebView
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      useMobileStore.getState().hydrateFromStorage()
      import('./sync').then(({ useSyncStore }) => {
        useSyncStore.getState().forcePull()
      }).catch(() => {})
    }
  })
}
